import { FC, useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import useSnackBars from "../commons/snackbar/SnackbarHook";
import useCancelToken from "../../hooks/useCancelToken";
import { outlookService, OutlookStatus } from "../../_services/outlook.service";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(3),
    maxWidth: 600,
    width: "100%",
    boxSizing: "border-box",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  outlookLogo: {
    width: 40,
    height: 40,
    flexShrink: 0,
  },
  statusContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  connectedIcon: {
    color: "#4caf50",
  },
  disconnectedIcon: {
    color: theme.palette.grey[400],
  },
  description: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
  },
  connectedInfo: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    "& > *:last-child": {
      wordBreak: "break-word",
      textAlign: "right",
      [theme.breakpoints.down("xs")]: {
        textAlign: "left",
        width: "100%",
      },
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
}));

const OutlookIntegration: FC = () => {
  const classes = useStyles();
  const { showSuccess, showError } = useSnackBars();
  const { getCancelToken, isCancel } = useCancelToken();

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [status, setStatus] = useState<OutlookStatus | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const result = await outlookService.getStatus();
      setStatus(result);
    } catch (error: any) {
      if (!isCancel(error)) {
        showError("Error al obtener estado de conexión con Outlook");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleConnect = async () => {
    setActionLoading(true);
    try {
      const { authUrl } = await outlookService.getAuthUrl();
      window.location.href = authUrl;
    } catch (error: any) {
      showError("Error al iniciar conexión con Outlook");
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setActionLoading(true);
    try {
      await outlookService.disconnect();
      setStatus({ connected: false, email: null, connectedAt: null });
      showSuccess("Outlook desconectado exitosamente");
    } catch (error: any) {
      showError("Error al desconectar Outlook");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("es-AR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box overflow="auto" height="100%">
      <Paper className={classes.container} elevation={0}>
        <Box className={classes.header}>
          <svg
            className={classes.outlookLogo}
            viewBox="0 0 48 48"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#0078D4"
              d="M28 4H8c-2.21 0-4 1.79-4 4v32c0 2.21 1.79 4 4 4h20c2.21 0 4-1.79 4-4V8c0-2.21-1.79-4-4-4z"
            />
            <path
              fill="#fff"
              d="M18 34c-5.52 0-10-4.48-10-10s4.48-10 10-10 10 4.48 10 10-4.48 10-10 10zm0-16c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6z"
            />
            <path
              fill="#28A8EA"
              d="M44 12H28v24h16c2.21 0 4-1.79 4-4V16c0-2.21-1.79-4-4-4z"
            />
            <path fill="#50D9FF" d="M28 12l12 12-12 12z" />
          </svg>
          <Typography variant="h6">Microsoft Outlook Calendar</Typography>
        </Box>

        <Typography className={classes.description}>
          Sincroniza automáticamente tus reservas de Webee con tu calendario de
          Microsoft Outlook. Cuando crees, modifiques o canceles una reserva, se
          actualizará en tu calendario personal.
        </Typography>

        <Divider />

        <Box className={classes.statusContainer}>
          {status?.connected ? (
            <>
              <CheckCircleIcon className={classes.connectedIcon} />
              <Typography>Conectado</Typography>
            </>
          ) : (
            <>
              <RadioButtonUncheckedIcon className={classes.disconnectedIcon} />
              <Typography>No conectado</Typography>
            </>
          )}
        </Box>

        {status?.connected && (
          <Box className={classes.connectedInfo}>
            <Box className={classes.infoRow}>
              <Typography variant="body2" color="textSecondary">
                Cuenta vinculada:
              </Typography>
              <Typography variant="body2">{status.email || "-"}</Typography>
            </Box>
            <Box className={classes.infoRow}>
              <Typography variant="body2" color="textSecondary">
                Conectado desde:
              </Typography>
              <Typography variant="body2">
                {formatDate(status.connectedAt)}
              </Typography>
            </Box>
          </Box>
        )}

        <Box className={classes.buttonContainer}>
          {status?.connected ? (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDisconnect}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : null}
            >
              {actionLoading ? "Desconectando..." : "Desconectar Outlook"}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleConnect}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : null}
            >
              {actionLoading ? "Conectando..." : "Conectar con Outlook"}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default OutlookIntegration;
